import { useEffect } from 'react';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  faqs: FAQItem[];
  title?: string;
  subtitle?: string;
  /** Unique id for the LD+JSON script tag to avoid duplicates */
  id?: string;
  /**
   * Whether this component injects FAQPage JSON-LD into <head>.
   * Set to false when the parent already renders FAQ structured data
   * (e.g. inline in the pre-rendered HTML) to avoid duplicate schema.
   */
  injectSchema?: boolean;
}

/**
 * Renders an FAQ accordion section with LD+JSON structured data for SEO.
 * Injects FAQPage schema into the document head automatically.
 */
export function FAQSection({ faqs, title = 'Frequently Asked Questions', subtitle, id = 'faq', injectSchema = true }: FAQSectionProps) {
  useEffect(() => {
    if (!injectSchema) return;
    const scriptId = `ld-json-faq-${id}`;
    // Remove existing script if re-rendered
    document.getElementById(scriptId)?.remove();

    const ldJson = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(ldJson);
    document.head.appendChild(script);

    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [faqs, id, injectSchema]);

  return (
    <section id={id} className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">{title}</h2>
          {subtitle && <p className="text-xl text-slate-600 dark:text-neutral-400 max-w-2xl mx-auto">{subtitle}</p>}
        </div>

        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <details
              key={i}
              className="group bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden hover:border-amber-200 dark:hover:border-amber-700/50 transition-colors"
            >
              <summary className="flex items-center justify-between cursor-pointer px-6 py-5 text-left">
                <span className="font-semibold text-slate-900 dark:text-white pr-4">{faq.question}</span>
                <span className="shrink-0 w-6 h-6 rounded-full bg-slate-100 dark:bg-neutral-800 group-open:bg-amber-100 dark:group-open:bg-amber-900/40 flex items-center justify-center transition-colors">
                  <svg
                    className="w-4 h-4 text-slate-500 dark:text-neutral-400 group-open:text-amber-600 dark:group-open:text-amber-400 group-open:rotate-180 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </summary>
              <div className="px-6 pb-5 text-slate-600 dark:text-neutral-400 leading-relaxed border-t border-slate-100 dark:border-neutral-800 pt-4">
                {faq.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
